import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, where, limit, getDocs, Timestamp } from 'firebase/firestore';


import { formatDistanceToNow } from 'date-fns';

const RESPONSE_LABEL = { yes: '👍 In', no: '👋 Out', maybe: '🤙 Maybe' };
const safeGet = async (q) => { try { return await getDocs(q); } catch { return null; } };

const COLLAPSED_COUNT = 5;
const FEED_LIMIT = 15;
// Scanned wider than FEED_LIMIT because the feed orders by last activity, not by
// creation: an RSVP created days ago but changed minutes ago belongs near the top,
// and ordering the query by `timestamp` alone would not surface it.
const RSVP_SCAN = 60;

export default function RecentActivity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const now = new Date();

        // No subscriber query here. It used to add "X subscribed to text
        // notifications" entries, but `subscribers` became admin-only when the
        // roster stopped being world-readable, so for a public visitor it could
        // only ever return permission-denied — a guaranteed-failing round trip on
        // every page load feeding a section that could never render. Restoring
        // those entries would need a public, non-identifying source, not a read
        // of that collection.
        const [rsvpSnap, attendanceSnap] = await Promise.all([
          safeGet(query(
            collection(db, 'rsvps'),
            orderBy('timestamp', 'desc'),
            limit(RSVP_SCAN)
          )),
          // Fetch recent past events and filter actualAttendance in JS to avoid composite index
          safeGet(query(
            collection(db, 'events'),
            where('date', '<', Timestamp.fromDate(now)),
            orderBy('date', 'desc'),
            limit(20)
          )),
        ]);

        // Show when the RSVP last changed, not when it was created. An SMS reply
        // that updates an existing RSVP only moves `updatedAt` — reading
        // `timestamp` made a reply from minutes ago read as days old.
        const rsvpItems = (rsvpSnap?.docs || []).map(d => ({
          id: d.id, _type: 'rsvp', _ts: d.data().updatedAt || d.data().timestamp, ...d.data()
        }));

        const attendanceItems = (attendanceSnap?.docs || [])
          .map(d => ({ id: d.id, _type: 'attendance', _ts: d.data().date, ...d.data() }))
          .filter(e => e.actualAttendance > 0)
          .slice(0, 5);

        const all = [...rsvpItems, ...attendanceItems]
          .filter(item => item._ts)
          .sort((a, b) => b._ts.toMillis() - a._ts.toMillis())
          .slice(0, FEED_LIMIT);

        setItems(all);
      } catch (e) {
        console.error('Error loading recent activity:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="recent-activity-empty">Loading recent activity...</p>;
  if (items.length === 0) return <p className="recent-activity-empty">No activity yet.</p>;

  const visibleItems = expanded ? items : items.slice(0, COLLAPSED_COUNT);

  return (
    <>
      <ul className="recent-activity-list">
        {visibleItems.map(item => {
          const when = item._ts?.toDate ? item._ts.toDate() : null;

          if (item._type === 'attendance') {
            return (
              <li key={`att-${item.id}`} className="recent-activity-item">
                <span className="recent-activity-name" style={{color:'var(--green)'}}>{item.actualAttendance} players</span>
                {' showed up 🥏'}
                {when && (
                  <span className="recent-activity-time"> · {formatDistanceToNow(when, { addSuffix: true })}</span>
                )}
              </li>
            );
          }

          // No 'subscriber' branch — see the note on the query above.

          // Default: RSVP
          const guestText = item.plusCount > 0
            ? ` +${item.plusCount} guest${item.plusCount > 1 ? 's' : ''}`
            : '';
          const channel = item.source === 'sms' ? '📱 text' : 'website';
          return (
            <li key={item.id} className="recent-activity-item">
              <span className="recent-activity-name">{item.name || 'Someone'}</span>
              {' RSVPed '}
              <span className={`recent-activity-response recent-activity-${item.response}`}>
                {RESPONSE_LABEL[item.response] || item.response}
              </span>
              {guestText}
              {' via '}{channel}
              {when && (
                <span className="recent-activity-time"> · {formatDistanceToNow(when, { addSuffix: true })}</span>
              )}
            </li>
          );
        })}
      </ul>
      {items.length > COLLAPSED_COUNT && (
        <button className="recent-activity-toggle" onClick={() => setExpanded(prev => !prev)}>
          {expanded ? 'Show less ▴' : `+${items.length - COLLAPSED_COUNT} more ▾`}
        </button>
      )}
    </>
  );
}
